import { apiClient } from '@/lib/apiClient';

/**
 * P6-12 / M11 — tickets API client.
 * Wraps /api/v1/platform/tickets endpoints. Mutating endpoints carry
 * an Idempotency-Key header per platform write discipline (each
 * mutation generates a fresh UUID per call).
 */

export type TicketStatus =
    | 'open'
    | 'in_progress'
    | 'waiting_customer'
    | 'waiting_internal'
    | 'waiting_on_user'
    | 'resolved'
    | 'closed';

export type TicketPriority = 'low' | 'normal' | 'medium' | 'high' | 'critical';

export type TicketType = 'new_feature' | 'bug_report' | 'general_inquiry';

export type TicketSourceApp = 'platform' | 'admin' | 'pos' | 'email' | 'widget' | 'api';

export type TicketCategory =
    | 'billing'
    | 'technical'
    | 'account'
    | 'feature_request'
    | 'onboarding'
    | 'other';

export type TicketChannel = 'email' | 'widget' | 'api' | 'admin';

export interface TicketDto {
    id: string;
    ticketNumber: string | null;
    tenantId: string;
    branchId?: string | null;
    userId?: string | null;
    sourceApp?: TicketSourceApp | null;
    type?: TicketType | null;
    subject: string;
    body: string | null;
    description?: string | null;
    status: TicketStatus;
    priority: TicketPriority;
    category: TicketCategory | null;
    aiSubcategory: string | null;
    tags: string[];
    channel: TicketChannel | null;
    openedByUserId: string | null;
    openedByEmail: string | null;
    assignedTo: string | null;
    openedAt: string;
    firstResponseAt: string | null;
    resolvedAt: string | null;
    closedAt: string | null;
    slaFirstResponseTargetAt: string | null;
    slaResolutionTargetAt: string | null;
    slaFirstResponseBreached: boolean;
    slaResolutionBreached: boolean;
    metadata?: Record<string, unknown> | null;
    resolutionNote?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface TicketReplyDto {
    id: string;
    ticketId: string;
    authorUserId: string | null;
    authorEmail: string | null;
    body: string;
    isInternal: boolean;
    createdAt: string;
}

export interface TicketEventDto {
    id: string;
    ticketId: string;
    tenantId: string;
    actorUserId: string | null;
    actorEmail: string | null;
    actorType: string;
    eventType: string;
    isInternal: boolean;
    details: Record<string, unknown>;
    createdAt: string;
}

/**
 * Server-side SLA filter states, mirrored from the backend predicates
 * (`TICKET_SLA_STATES` in tickets.validator.ts). `on_track` is the server
 * name for the queue's local "healthy" SLA badge state.
 */
export const TICKET_SLA_FILTERS = ['breached', 'at_risk', 'on_track'] as const;
export type TicketSlaFilter = (typeof TICKET_SLA_FILTERS)[number];

export function parseTicketSlaFilter(value: string | null | undefined): TicketSlaFilter | '' {
    return (TICKET_SLA_FILTERS as readonly string[]).includes(value ?? '') ? (value as TicketSlaFilter) : '';
}

export interface ListTicketsParams {
    status?: TicketStatus;
    assignedTo?: string;
    category?: string;
    type?: TicketType;
    priority?: TicketPriority;
    tenantId?: string;
    branchId?: string;
    sourceApp?: TicketSourceApp;
    slaState?: TicketSlaFilter;
    search?: string;
    page?: number;
    pageSize?: number;
}

export interface TicketQueueSummary {
    open: number;
    breached: number;
    at_risk: number;
    unassigned: number;
}

export interface ListTicketsResult {
    tickets: TicketDto[];
    page: number;
    pageSize: number;
    /** Queue-wide KPI counts. Older backends omit it; the UI then shows a dash. */
    summary?: TicketQueueSummary;
}

function genIdempotencyKey(): string {
    return crypto.randomUUID();
}

export async function listTickets(params: ListTicketsParams = {}): Promise<ListTicketsResult> {
    const { data } = await apiClient.get<{ data: ListTicketsResult }>('/tickets', { params });
    return data.data;
}

export async function getTicket(id: string): Promise<{ ticket: TicketDto; replies: TicketReplyDto[]; events: TicketEventDto[] }> {
    const { data } = await apiClient.get<{ data: { ticket: TicketDto; replies: TicketReplyDto[]; events: TicketEventDto[] } }>(
        `/tickets/${id}`,
    );
    return data.data;
}

export async function updateTicket(
    id: string,
    patch: { status?: TicketStatus; priority?: TicketPriority; category?: TicketCategory; tags?: string[] },
): Promise<TicketDto> {
    const { data } = await apiClient.patch<{ data: TicketDto }>(`/tickets/${id}`, patch, {
        headers: { 'Idempotency-Key': genIdempotencyKey() },
    });
    return data.data;
}

export async function addReply(
    id: string,
    body: { body: string; isInternal?: boolean },
): Promise<TicketReplyDto> {
    const { data } = await apiClient.post<{ data: TicketReplyDto }>(`/tickets/${id}/replies`, body, {
        headers: { 'Idempotency-Key': genIdempotencyKey() },
    });
    return data.data;
}

export async function addInternalNote(id: string, body: { body: string }): Promise<TicketReplyDto> {
    const { data } = await apiClient.post<{ data: TicketReplyDto }>(`/tickets/${id}/internal-notes`, body, {
        headers: { 'Idempotency-Key': genIdempotencyKey() },
    });
    return data.data;
}

export async function assignTicket(id: string, userId: string | null): Promise<TicketDto> {
    const { data } = await apiClient.post<{ data: TicketDto }>(
        `/tickets/${id}/assign`,
        { userId },
        { headers: { 'Idempotency-Key': genIdempotencyKey() } },
    );
    return data.data;
}

export async function resolveTicket(id: string, resolutionNote: string): Promise<TicketDto> {
    const { data } = await apiClient.post<{ data: TicketDto }>(
        `/tickets/${id}/resolve`,
        { resolutionNote },
        { headers: { 'Idempotency-Key': genIdempotencyKey() } },
    );
    return data.data;
}

export async function closeTicket(id: string): Promise<TicketDto> {
    const { data } = await apiClient.post<{ data: TicketDto }>(
        `/tickets/${id}/close`,
        {},
        { headers: { 'Idempotency-Key': genIdempotencyKey() } },
    );
    return data.data;
}

export async function setSupportOffDuty(supportOffDuty: boolean): Promise<{ supportOffDuty: boolean }> {
    const { data } = await apiClient.patch<{ data: { supportOffDuty: boolean } }>(
        '/me/support-status',
        { supportOffDuty },
    );
    return data.data;
}
